import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSection } from '../components/layout';
import { ClientDataSection } from '../components/forms/ClientDataSection';
import { PatrimonySection } from '../components/forms/PatrimonySection';
import { ProductionSection } from '../components/forms/ProductionSection';
import { DebtSection } from '../components/forms/DebtSection';
import {
  CreditQuestionnaireForm as CreditQuestionnaireFormData,
  ClientData,
  GroupMember,
  LandProperty,
  Stock,
  SupplierDebt,
  TradingDebt,
  BankDebt,
  CreditLimit,
  ThirdPartyDebt,
} from '../types';
import type { UploadedFileInfo } from '../services/api';

export interface CreditQuestionnaireFormProps {
  data: Partial<CreditQuestionnaireFormData>;
  onChange: (data: Partial<CreditQuestionnaireFormData>) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
  prefilledName?: string;
  prefilledCpf?: string;
  prefilledPhone?: string;
  token?: string;
}

export const CreditQuestionnaireForm: React.FC<CreditQuestionnaireFormProps> = ({
  data,
  onChange,
  errors = {},
  disabled = false,
  prefilledName = '',
  prefilledCpf = '',
  prefilledPhone = '',
  token = '',
}) => {
  const { t } = useTranslation();

  // Estado N/A para subseções que suportam marcação de não aplicável
  const [naFields, setNaFields] = useState<Record<string, boolean>>({});

  // Handler genérico para flags N/A
  const handleNaChange = useCallback((key: string, value: boolean) => {
    setNaFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ============================================
  // Section 1 — Dados do Cliente
  // ============================================

  // Propaga alteração de campo simples em clientData
  const handleClientDataChange = useCallback(
    (field: keyof ClientData) => (value: string | boolean) => {
      const newClientData = {
        ...data.clientData,
        [field]: value,
      } as ClientData;

      // Limpar membros do grupo ao desmarcar plantio em grupo
      if (field === 'plantsInGroup' && !value) {
        newClientData.groupMembers = [];
      }

      onChange({ ...data, clientData: newClientData });
    },
    [data, onChange]
  );

  // Propaga múltiplos campos de clientData em uma única chamada
  const handleBulkClientDataChange = useCallback(
    (fields: Partial<ClientData>) => {
      onChange({
        ...data,
        clientData: { ...data.clientData, ...fields } as ClientData,
      });
    },
    [data, onChange]
  );

  // Propaga alteração nos campos de preferência de contato
  const handleContactPreferenceChange = useCallback(
    (field: string) => (value: string | boolean) => {
      onChange({
        ...data,
        clientData: {
          ...data.clientData,
          contactPreference: {
            ...data.clientData?.contactPreference,
            [field]: value,
          },
        } as ClientData,
      });
    },
    [data, onChange]
  );

  // Propaga lista de membros do grupo atualizada
  const handleGroupMembersChange = useCallback(
    (groupMembers: GroupMember[]) => {
      onChange({
        ...data,
        clientData: { ...data.clientData, groupMembers } as ClientData,
      });
    },
    [data, onChange]
  );

  // ============================================
  // Section 2 — Patrimônio
  // ============================================

  const handleLandPropertiesChange = useCallback(
    (landProperties: LandProperty[]) => {
      onChange({
        ...data,
        patrimony: { ...data.patrimony, landProperties } as CreditQuestionnaireFormData['patrimony'],
      });
    },
    [data, onChange]
  );

  const handlePatrimonyFileUpload = useCallback(
    (fileInfo: UploadedFileInfo) => {
      onChange({
        ...data,
        patrimony: {
          ...data.patrimony,
          patrimonyFile: fileInfo,
        } as CreditQuestionnaireFormData['patrimony'],
      });
    },
    [data, onChange]
  );

  // ============================================
  // Section 4 — Produção
  // ============================================

  const handleProductionChange = useCallback(
    (productionData: Partial<CreditQuestionnaireFormData['production']>) => {
      onChange({
        ...data,
        production: {
          ...data.production,
          ...productionData,
        } as CreditQuestionnaireFormData['production'],
      });
    },
    [data, onChange]
  );

  const handleStockChange = useCallback(
    (stock: Stock[]) => {
      onChange({
        ...data,
        production: { ...data.production, stock } as CreditQuestionnaireFormData['production'],
      });
    },
    [data, onChange]
  );

  // ============================================
  // Section 7 — Endividamento
  // ============================================

  const handleSupplierDebtsChange = useCallback(
    (supplierDebts: SupplierDebt[]) => {
      onChange({
        ...data,
        debt: { ...data.debt, supplierDebts } as CreditQuestionnaireFormData['debt'],
      });
    },
    [data, onChange]
  );

  const handleTradingDebtsChange = useCallback(
    (tradingDebts: TradingDebt[]) => {
      onChange({
        ...data,
        debt: { ...data.debt, tradingDebts } as CreditQuestionnaireFormData['debt'],
      });
    },
    [data, onChange]
  );

  const handleBankDebtsChange = useCallback(
    (bankDebts: BankDebt[]) => {
      onChange({
        ...data,
        debt: { ...data.debt, bankDebts } as CreditQuestionnaireFormData['debt'],
      });
    },
    [data, onChange]
  );

  const handleCreditLimitsChange = useCallback(
    (creditLimits: CreditLimit[]) => {
      onChange({
        ...data,
        debt: { ...data.debt, creditLimits } as CreditQuestionnaireFormData['debt'],
      });
    },
    [data, onChange]
  );

  const handleThirdPartyDebtsChange = useCallback(
    (thirdPartyDebts: ThirdPartyDebt[]) => {
      onChange({
        ...data,
        debt: { ...data.debt, thirdPartyDebts } as CreditQuestionnaireFormData['debt'],
      });
    },
    [data, onChange]
  );

  return (
    <div className="space-y-6">
      {/* Seção 1: Dados do Cliente */}
      <FormSection
        title={t('questionnaire.section1.title')}
        description={t('questionnaire.section1.description')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        }
      >
        <ClientDataSection
          data={data.clientData || {}}
          onChange={handleClientDataChange}
          onBulkChange={handleBulkClientDataChange}
          onGroupMembersChange={handleGroupMembersChange}
          onContactPreferenceChange={handleContactPreferenceChange}
          errors={errors}
          disabled={disabled}
          prefilledName={prefilledName}
          prefilledCpf={prefilledCpf}
          prefilledPhone={prefilledPhone}
        />
      </FormSection>

      {/* Seção 2: Patrimônio */}
      <FormSection
        title={t('questionnaire.section2.title')}
        description={t('questionnaire.section2.description')}
        collapsible
        defaultExpanded={false}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
        }
      >
        <PatrimonySection
          data={data.patrimony || {}}
          onLandPropertiesChange={handleLandPropertiesChange}
          onFileUpload={handlePatrimonyFileUpload}
          disabled={disabled}
          token={token}
        />
      </FormSection>

      {/* Seção 4: Informação de Produção */}
      <FormSection
        title={t('questionnaire.section4.title')}
        description={t('questionnaire.section4.description')}
        collapsible
        defaultExpanded={false}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
      >
        <ProductionSection
          data={data.production || {}}
          onChange={handleProductionChange}
          onStockChange={handleStockChange}
          naFields={naFields}
          onNaChange={handleNaChange}
          disabled={disabled}
        />
      </FormSection>

      {/* Seção 7: Endividamento */}
      <FormSection
        title={t('questionnaire.section7.title')}
        description={t('questionnaire.section7.description')}
        collapsible
        defaultExpanded={false}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z"
            />
          </svg>
        }
      >
        <DebtSection
          data={data.debt || {}}
          onSupplierDebtsChange={handleSupplierDebtsChange}
          onTradingDebtsChange={handleTradingDebtsChange}
          onBankDebtsChange={handleBankDebtsChange}
          onCreditLimitsChange={handleCreditLimitsChange}
          onThirdPartyDebtsChange={handleThirdPartyDebtsChange}
          naFields={naFields}
          onNaChange={handleNaChange}
          disabled={disabled}
        />
      </FormSection>
    </div>
  );
};

export default CreditQuestionnaireForm;
